import { useCallback, useRef, useState } from "react";
import { resetWorker } from "@/lib/worker";
import { transcribeFile } from "@/lib/pipeline";
import { markBadModel } from "@/lib/models";

const IDLE = { stage: "idle", pct: 0, detail: "", error: null };
const MOBILE = /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);

export function useTranscriber() {
  const [state, setState] = useState(IDLE);
  const cache = useRef({ file: null, audio: null });
  const cancelRef = useRef(false);

  const run = useCallback(async (opts) => {
    cancelRef.current = false;
    const t0 = performance.now();
    try {
      const { segs, stats } = await transcribeFile({
        ...opts, cache: cache.current, isCancelled: () => cancelRef.current,
        onState: (s) => setState({ ...IDLE, ...s }),
      });
      if (MOBILE) resetWorker("Model released to free phone memory");
      const secs = ((performance.now() - t0) / 1000).toFixed(1);
      const wt = stats.wordTiming ? (stats.wordFallback ? " Word timing unavailable on this device — using estimates." : " With exact word timing.") : "";
      setState({ ...IDLE, stage: "done", pct: 1, detail: `${segs.length} captions generated in ${secs}s by ${opts.cfg.label}.${wt}` });
      return segs;
    } catch (e) {
      const code = e.code || (e instanceof RangeError ? "OOM" : "UNKNOWN");
      setState({ ...IDLE, stage: "error", error: { code, message: e.message || String(e) } });
      if (["OOM", "WORKER", "GPU"].includes(code)) {
        markBadModel(opts.cfg.cacheKey);
        resetWorker("Engine reset after error");
      }
      return null;
    }
  }, []);

  const cancel = useCallback(() => {
    cancelRef.current = true;
    resetWorker("Transcription stopped.");
  }, []);

  const clear = useCallback(() => {
    cache.current = { file: null, audio: null };
    setState(IDLE);
  }, []);

  return { state, run, cancel, clear };
}
